import { createElement, type ComponentType } from 'react'
import App from '../App'
import PotencialPage from '../PotencialPage'
import PrimeiraConversaPage from '../PrimeiraConversaPage'
import AcompanhamentoPage from '../AcompanhamentoPage'
import CodexWindowsPage from '../CodexWindowsPage'
import NotFoundPage from '../NotFoundPage'
import PrivacyPage from '../PrivacyPage'
import ProfessionPage from '../ProfessionPage'
import EmpreendedoresPage from '../EmpreendedoresPage'
import TermsPage from '../TermsPage'
import ValoresPage from '../ValoresPage'
import { professions } from '../professions'
import type { ProfessionPageConfig } from '../professions/types'
import { acompanhamentoSeo, conversaSeo, learningStructuredDataScript, potencialSeo } from '../seo/learningPages'
import { codexWindowsDescription, codexWindowsStructuredDataScript, codexWindowsTitle } from '../seo/codexWindowsPage'
import { professionStructuredDataScript } from '../seo/professionSeo'
import { empreendedoresDescription, empreendedoresStructuredDataScript, empreendedoresTitle } from '../seo/empreendedoresPage'
import { homeDescription, homeTitle, structuredDataScript } from '../seo/structuredData'
import { valoresDescription, valoresStructuredDataScript, valoresTitle } from '../seo/valoresPage'
import {
  codexWindowsPath,
  codexWindowsWhatsAppMessage,
  empreendedoresPath,
  empreendedoresWhatsAppMessage,
  legalPaths,
  siteWhatsAppMessage,
  valoresPath,
  valoresWhatsAppMessage,
} from '../siteDestinations'

/**
 * Registro das páginas públicas: fonte única de caminho, título, descrição,
 * data de edição, mensagem do WhatsApp e ficha JSON-LD. Alimenta o roteador
 * no navegador, o pré-render do build (scripts/prerender.mjs) e o sitemap.
 */
export interface PageDefinition {
  /** Caminho público, sempre com barra final (a raiz é '/'; a página de erro é '/404.html'). */
  path: string
  title: string
  description: string
  /** Data da última edição editorial, usada no sitemap. */
  lastmod: string
  /** Só a página de erro fica fora do índice. */
  robots: 'index,follow' | 'noindex'
  inSitemap: boolean
  /** Mensagem pré-preenchida dos links de WhatsApp da página (botão flutuante e rodapé). */
  whatsappMessage: string
  /** Texto alternativo da imagem de compartilhamento. */
  imageAlt: string
  /** Ficha JSON-LD da página, injetada no <head> pelo pré-render. */
  structuredData?: () => string
  Component: ComponentType
}

const shareImageAltHome = 'Oficina IA: curso de IA para iniciantes, online' // termo-ok: palavra buscada no Google (decisão de SEO)

export const HOME_PAGE: PageDefinition = {
  path: '/',
  title: homeTitle,
  description: homeDescription,
  lastmod: '2026-09-25',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: siteWhatsAppMessage,
  imageAlt: shareImageAltHome,
  structuredData: structuredDataScript,
  Component: App,
}

/** Novos destinos de descoberta e decisão, com conteúdo e ficha de busca próprios. */
const learningPage = (seo: typeof potencialSeo | typeof conversaSeo | typeof acompanhamentoSeo, Component: ComponentType): PageDefinition => ({
  path: seo.path,
  title: seo.title,
  description: seo.description,
  lastmod: '2026-09-25',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: seo.whatsappMessage,
  imageAlt: seo.title,
  structuredData: () => learningStructuredDataScript(seo),
  Component,
})

export const POTENCIAL_PAGE = learningPage(potencialSeo, PotencialPage)
export const CONVERSA_PAGE = learningPage(conversaSeo, PrimeiraConversaPage)
export const ACOMPANHAMENTO_PAGE = learningPage(acompanhamentoSeo, AcompanhamentoPage)

export const EMPREENDEDORES_PAGE: PageDefinition = {
  path: empreendedoresPath,
  title: empreendedoresTitle,
  description: empreendedoresDescription,
  lastmod: '2026-09-28',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: empreendedoresWhatsAppMessage,
  imageAlt: 'Oficina IA para empreendedores: inteligência artificial nas tarefas do seu negócio',
  structuredData: empreendedoresStructuredDataScript,
  Component: EmpreendedoresPage,
}

export const VALORES_PAGE: PageDefinition = {
  path: valoresPath,
  title: valoresTitle,
  description: valoresDescription,
  lastmod: '2026-09-25',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: valoresWhatsAppMessage,
  imageAlt: shareImageAltHome,
  structuredData: valoresStructuredDataScript,
  Component: ValoresPage,
}

export const CODEX_WINDOWS_PAGE: PageDefinition = {
  path: codexWindowsPath,
  title: codexWindowsTitle,
  description: codexWindowsDescription,
  lastmod: '2026-09-25',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: codexWindowsWhatsAppMessage,
  imageAlt: 'Oficina IA: como instalar o Codex no Windows',
  structuredData: codexWindowsStructuredDataScript,
  Component: CodexWindowsPage,
}

/** Uma página por profissão (passo 9 do SEO), montada a partir de src/professions/<profissão>.ts. */
const professionPage = (config: ProfessionPageConfig): PageDefinition => ({
  path: config.path,
  title: config.title,
  description: config.description,
  lastmod: config.lastmod,
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: config.whatsappMessage,
  imageAlt: config.imageAlt,
  structuredData: () => professionStructuredDataScript(config),
  Component: () => createElement(ProfessionPage, { config }),
})

export const PROFESSION_PAGES: readonly PageDefinition[] = Object.freeze(professions.map(professionPage))

export const PRIVACY_PAGE: PageDefinition = {
  path: legalPaths.privacy,
  title: 'Política de Privacidade | Oficina IA',
  description: 'Saiba como a Oficina IA trata os dados do site, do Rogério e da medição de anúncios, com escolha de cookies, armazenamento e seus direitos.',
  lastmod: '2026-09-25',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: siteWhatsAppMessage,
  imageAlt: shareImageAltHome,
  Component: PrivacyPage,
}

export const TERMS_PAGE: PageDefinition = {
  path: legalPaths.terms,
  title: 'Termos de Uso | Oficina IA',
  description: 'Conheça os termos de uso da Oficina IA, as ideias e guias do Rogério, os limites do serviço e as condições dos acompanhamentos.',
  lastmod: '2026-09-25',
  robots: 'index,follow',
  inSitemap: true,
  whatsappMessage: siteWhatsAppMessage,
  imageAlt: shareImageAltHome,
  Component: TermsPage,
}

export const NOT_FOUND_PAGE: PageDefinition = {
  path: '/404.html',
  title: 'Página não encontrada | Oficina IA',
  description: 'O endereço aberto não corresponde a nenhuma página da Oficina IA.',
  lastmod: '2026-09-04',
  robots: 'noindex',
  inSitemap: false,
  whatsappMessage: siteWhatsAppMessage,
  imageAlt: shareImageAltHome,
  Component: NotFoundPage,
}

export const PAGES: readonly PageDefinition[] = Object.freeze([
  HOME_PAGE,
  EMPREENDEDORES_PAGE,
  POTENCIAL_PAGE,
  CONVERSA_PAGE,
  ACOMPANHAMENTO_PAGE,
  VALORES_PAGE,
  CODEX_WINDOWS_PAGE,
  ...PROFESSION_PAGES,
  PRIVACY_PAGE,
  TERMS_PAGE,
  NOT_FOUND_PAGE,
])

export const SITEMAP_PAGES = PAGES.filter((page) => page.inSitemap)

/** "/base", "/base/" e "/base/index.html" são a mesma página; qualquer outro desvio é página não encontrada. */
export const normalizePath = (pathname: string): string => {
  if (pathname === NOT_FOUND_PAGE.path) return pathname
  const clean = pathname.replace(/\/index\.html$/, '').replace(/\/+$/, '')
  return clean === '' ? '/' : `${clean}/`
}

export const findPage = (pathname: string): PageDefinition => {
  const normalized = normalizePath(pathname)
  // O Pages faz o redirecionamento permanente; este alias cobre o servidor local.
  const path = ['/50-mais/', '/60-mais/'].includes(normalized) ? empreendedoresPath : normalized
  return PAGES.find((page) => page.path === path) ?? NOT_FOUND_PAGE
}
